import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Globe, ChevronDown } from "lucide-react";
import { useLang } from "@/LangContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const LANGS = [
  { code: "en", label: "English" },
  { code: "nl", label: "Nederlands" },
  { code: "it", label: "Italiano" },
  { code: "es", label: "Español" },
];

export default function Navbar() {
  const { locale, setLocale, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "approach", label: t.nav.about },
    { id: "testimonials", label: t.nav.testimonials },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-background/70 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="logo-home"
          onClick={() => scrollTo("top")}
          className="font-serif text-2xl tracking-tight text-foreground"
        >
          Cozy Home
          <span className="text-accent">.</span>
        </button>

        <div className="hidden md:flex items-center gap-10 text-sm">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-foreground/70 hover:text-foreground transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <button
              data-testid="lang-trigger"
              onClick={() => setLangOpen((o) => !o)}
              className="flex items-center gap-1.5 text-xs tracking-widest uppercase border border-border px-3 py-2 hover:bg-foreground hover:text-background transition-colors duration-300"
            >
              <Globe className="w-3.5 h-3.5" />
              {locale}
              <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                <div className="absolute right-0 mt-2 w-40 bg-background border border-border shadow-xl z-50" data-testid="lang-menu">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onClick={() => { setLocale(l.code); setLangOpen(false); }}
                      className={`block w-full text-left px-4 py-2.5 text-sm hover:bg-muted transition-colors duration-200 ${locale === l.code ? "text-primary font-medium" : "text-foreground/80"}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <a
            data-testid="nav-book-now"
            href="https://pulisci-facile.emergent.host"
            className="inline-flex items-center text-sm bg-primary text-white px-5 py-2.5 hover:bg-foreground transition-colors duration-300"
          >
            {t.nav.book}
          </a>
        </div>
      </nav>
    </motion.header>
  );
}
